import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Tag } from "@/uiKit/atoms/Tag.tsx";

type NurseNoticeButtonProps = {
  patientId: string;
  className?: string;
};

export const NurseNoticeButton: React.FC<NurseNoticeButtonProps> = ({ patientId, className }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const deleteNotice = Queries.observations.useDeleteNotice();
  const createNotice = Queries.observations.useCreateNotice();

  const notice = Queries.practitioner.useNurseNotice(patientId);

  if (!notice) {
    return (
      <div className={cx(styles.container, className)}>
        <Button
          data-action="medical-file.notice.create"
          variant="contained"
          startIcon={<Icons.forward className={styles.icon} />}
          pending={createNotice.isPending}
          onClick={() =>
            createNotice.mutate({
              patient_id: patientId,
            })
          }
        >
          {t("patientNavigation.notice.notifyDoctor")}
        </Button>
      </div>
    );
  }

  return (
    <div className={cx(styles.container, className)}>
      <Tag
        color="neutral"
        label={t("patientNavigation.notice.notified")}
        icon={<Icons.forward className={styles.icon} />}
        className={styles.tag}
      />
      <Button
        variant="outlined"
        data-action="medical-file.notice.delete"
        pending={deleteNotice.isPending}
        onClick={() =>
          deleteNotice.mutate({
            patient_id: patientId,
            id: notice.id,
          })
        }
      >
        {t("patientNavigation.notice.cancelNotify")}
      </Button>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(4),
  }),
  tag: css`
    padding: ${theme.spacing(8)};
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
